// @ts-strict-ignore
import {EventDefinition, EventParams, MpEvent, PropertyDefinition, PropertyParams} from 'common/util/definitions-types';
import {PropertyType} from 'common/report/constants';

// @ts-check
import {getProjectId, getWorkspaceId} from '../report/util';
import {eventDefinitionsClient} from '../report/queries/app-api/event-definitions-client';
import {propertyDefinitionsClient} from '../report/queries/app-api/property-definitions-client';

function fetchDefinitions(
  queryClient,
  params: EventParams | PropertyParams = {},
  options: Record<string, any> = {},
): Promise<any> {
  const projectId = params.projectId || getProjectId();
  const workspaceId = params.workspaceId || getWorkspaceId();
  return queryClient.query({...params, projectId, workspaceId}, options);
}

export async function fetchEventDefinitions(
  params: EventParams = {},
  options: Record<string, any> = {},
): Promise<EventDefinition[]> {
  return fetchDefinitions(eventDefinitionsClient, params, options);
}

export function fetchPropertyDefinitions(
  params: PropertyParams,
  options: Record<string, any> = {},
): Promise<PropertyDefinition | PropertyDefinition[]> {
  return fetchDefinitions(propertyDefinitionsClient, params, options);
}

export function getDefinitionForEvent(mpEvent: MpEvent, eventDefinitions: EventDefinition[]): EventDefinition | null {
  let predicate = (definition) => {
    return (
      (definition.name === mpEvent.name || definition.name === mpEvent.value) &&
      !definition.collectEverythingEventId &&
      !definition.customEventId
    );
  };

  // look if the mpEvent is merged to a different event

  const mergedEventDef = eventDefinitions.find(({mergeEvents}) => {
    return mergeEvents?.includes(mpEvent.name) || mergeEvents?.includes(mpEvent.value);
  });

  if (mergedEventDef) {
    return {
      ...mergedEventDef,
      displayName: mpEvent.name || mpEvent.value,
      mergedTo: mergedEventDef.name,
    };
  }

  if (mpEvent.is_collect_everything_event) {
    predicate = (definition) => definition.collectEverythingEventId === mpEvent.id;
  } else if (mpEvent.custom) {
    predicate = (definition) => definition.customEventId === mpEvent.id;
  }

  const eventDefinition = eventDefinitions.find(predicate);
  return eventDefinition ? eventDefinition : null;
}

export function getDefinitionForProperty(
  {
    name,
    customPropertyId,
    dataGroupId,
  }: {
    name: string;
    customPropertyId?: string;
    dataGroupId?: string;
  },
  propertyDefinitions: PropertyDefinition[],
): PropertyDefinition | null {
  // look if the property is merged to a different property

  const mergedPropertyDef = propertyDefinitions.find(({mergeProperties}) => {
    return mergeProperties?.includes(name);
  });

  if (mergedPropertyDef) {
    const result = {
      ...mergedPropertyDef,
      displayName: name,
      mergedTo: mergedPropertyDef.name,
    };

    if (dataGroupId) {
      result[`type`] = PropertyType.Dimension;
      result[`dimension_data_group_id`] = dataGroupId;
    }

    return result;
  }

  let propertyDefinition = propertyDefinitions.find((definition) => {
    const definitionIsCustom = definition.status === `custom`;
    const propertyIsCustom = !!customPropertyId;
    // both need to be custom or non-custom at the same time
    if (definitionIsCustom === propertyIsCustom) {
      if (definitionIsCustom) {
        return definition.customPropertyId.toString() === customPropertyId.toString();
      }
      return definition.name === name;
    }
    return false;
  });

  if (propertyDefinition && dataGroupId) {
    propertyDefinition = {
      ...propertyDefinition,
      type: PropertyType.Dimension,
      dimension_data_group_id: dataGroupId,
    };
  }

  return propertyDefinition ? propertyDefinition : null;
}
